import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { setUnauthenticatedHandler } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/lib/theme';
import { AppShell } from '@/components/layout/AppShell';
import { DashboardPage } from '@/pages/DashboardPage';
import { FundDetailPage } from '@/pages/FundDetailPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlanPage } from '@/pages/PlanPage';
import { PlanCalendarPage } from '@/pages/PlanCalendarPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { StatementPage } from '@/pages/StatementPage';
import { TransactionsPage } from '@/pages/TransactionsPage';

export function App(): JSX.Element {
  const { status, bootstrap, clear } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Restore the session from the refresh cookie on first load.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // A refresh that fails anywhere in the app lands here.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      clear();
      navigate('/login', { replace: true });
    });
  }, [clear, navigate]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={status === 'authenticated' ? <AppShell /> : <Navigate to="/login" replace />}>
          <Route index element={<DashboardPage />} />
          <Route path="funds/:id" element={<FundDetailPage />} />
          <Route path="funds/:id/statement" element={<StatementPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="plan/calendar" element={<PlanCalendarPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      <Toaster
        theme={theme}
        position="bottom-center"
        richColors
        closeButton
        // Clear of the mobile tab bar and the floating add button.
        offset={16}
        toastOptions={{ style: { marginBottom: 'calc(4rem + env(safe-area-inset-bottom))' } }}
      />
    </>
  );
}
